import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import jobService from '../../services/jobService';
import { getCompanyVerificationError } from '../../utils/errorHandler';
import './MyJobs.css';

const MyJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [filter, currentPage]);

  // Temporarily disabled click outside handler for testing
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (activeDropdown && !event.target.closest('.action-dropdown')) {
  //       setActiveDropdown(null);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [activeDropdown]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(filter !== 'all' && { status: filter }),
        ...(searchQuery && { search: searchQuery })
      };
      const response = await jobService.getMyJobs(params);
      setJobs(response.data || []);
      setPagination(response.pagination || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      const errorMessage = getCompanyVerificationError(error);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await jobService.getJobStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      console.log('Updating job status:', jobId, newStatus);
      await jobService.updateJobStatus(jobId, newStatus);
      toast.success('Job status updated successfully');
      fetchJobs();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      console.error('Error response:', error.response);
      const errorMessage = getCompanyVerificationError(error);
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await jobService.deleteJob(jobId);
      toast.success('Job deleted successfully');
      fetchJobs();
      fetchStats();
    } catch (error) {
      console.error('Error deleting job:', error);
      const errorMessage = getCompanyVerificationError(error);
      toast.error(errorMessage);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchJobs();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleDropdown = (jobId) => {
    setActiveDropdown(activeDropdown === jobId ? null : jobId);
  };

  const handleActionClick = (action, jobId) => {
    console.log('Action clicked:', action, 'Job ID:', jobId);
    setActiveDropdown(null);
    if (action === 'edit') {
      navigate(`/employer/jobs/${jobId}/edit`);
    } else if (action === 'view') {
      // navigate(`/employer/jobs/${jobId}`);
    } else if (action === 'applications') {
      navigate(`/employer/jobs/${jobId}/applications`);
    } else if (action === 'publish') {
      handleStatusChange(jobId, 'active');
    } else if (action === 'hold') {
      console.log('Putting job on hold:', jobId);
      handleStatusChange(jobId, 'on_hold');
    } else if (action === 'close') {
      handleStatusChange(jobId, 'closed');
    } else if (action === 'reopen') {
      handleStatusChange(jobId, 'active');
    } else if (action === 'delete') {
      handleDelete(jobId);
    }
  };

  const renderPagination = () => {
    if (!pagination.pages || pagination.pages <= 1) return null;

    const pages = [];
    const currentPageNum = pagination.page || 1;
    const totalPages = pagination.pages;

    // Always show first page
    pages.push(1);

    // Show ellipsis or pages around current page
    if (currentPageNum > 3) {
      pages.push('...');
    }

    // Show pages around current page
    for (let i = Math.max(2, currentPageNum - 1); i <= Math.min(totalPages - 1, currentPageNum + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // Show ellipsis before last page
    if (currentPageNum < totalPages - 2) {
      pages.push('...');
    }

    // Always show last page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return (
      <div className="pagination-container">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPageNum - 1)}
          disabled={currentPageNum === 1}
        >
          ← Previous
        </button>

        <div className="pagination-numbers">
          {pages.map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
            ) : (
              <button
                key={page}
                className={`pagination-number ${currentPageNum === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            )
          ))}
        </div>

        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPageNum + 1)}
          disabled={currentPageNum === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      draft: 'badge-warning',
      closed: 'badge-danger',
      'on_hold': 'badge-secondary',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-secondary';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  const statusConfig = {
    active:  { label: 'Active',   color: 'green',  icon: 'check_circle' },
    draft:   { label: 'Draft',    color: 'gray',   icon: 'edit_note' },
    closed:  { label: 'Closed',   color: 'red',    icon: 'lock' },
    on_hold: { label: 'On Hold',  color: 'orange', icon: 'pause_circle' },
  };

  const getStatusCfg = (s) => statusConfig[s] || statusConfig.draft;

  if (loading) {
    return (
      <div className="emp-my-jobs mj-container">
        <div className="mj-loading">
          <div className="mj-spinner"></div>
          <p>Loading jobs…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-my-jobs mj-container">
      {/* Header */}
      <div className="mj-header">
        <div>
          <h1 className="mj-title">My Jobs</h1>
          <p className="mj-subtitle">Manage and track all your job postings</p>
        </div>
        <Link to="/employer/post-job" className="mj-cta-btn">
          <span className="material-symbols-outlined">add</span>
          Post New Job
        </Link>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mj-stats">
          <div className="mj-stat blue">
            <div className="mj-stat-icon"><span className="material-symbols-outlined">work</span></div>
            <div className="mj-stat-content">
              <div className="mj-stat-num">{stats.totalJobs || 0}</div>
              <div className="mj-stat-label">Total Jobs</div>
            </div>
          </div>
          <div className="mj-stat green">
            <div className="mj-stat-icon"><span className="material-symbols-outlined">check_circle</span></div>
            <div className="mj-stat-content">
              <div className="mj-stat-num">{stats.activeJobs || 0}</div>
              <div className="mj-stat-label">Active</div>
            </div>
          </div>
          <div className="mj-stat purple">
            <div className="mj-stat-icon"><span className="material-symbols-outlined">visibility</span></div>
            <div className="mj-stat-content">
              <div className="mj-stat-num">{stats.totalViews || 0}</div>
              <div className="mj-stat-label">Total Views</div>
            </div>
          </div>
          <div className="mj-stat orange">
            <div className="mj-stat-icon"><span className="material-symbols-outlined">group</span></div>
            <div className="mj-stat-content">
              <div className="mj-stat-num">{stats.totalApplications || 0}</div>
              <div className="mj-stat-label">Applications</div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mj-toolbar">
        <form onSubmit={handleSearch} className="mj-search">
          <span className="material-symbols-outlined">search</span>
          <input
            type="text"
            placeholder="Search by title…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="mj-tabs">
          {[
            { key: 'all',    label: 'All',     count: stats?.totalJobs  || 0 },
            { key: 'active', label: 'Active',  count: stats?.activeJobs || 0, dot: 'green' },
            { key: 'draft',  label: 'Draft',   count: null,                   dot: 'gray' },
            { key: 'closed', label: 'Closed',  count: null,                   dot: 'red' },
          ].map(t => (
            <button
              key={t.key}
              className={`mj-tab ${filter === t.key ? 'active' : ''}`}
              onClick={() => { setFilter(t.key); setCurrentPage(1); }}
            >
              {t.dot && <span className={`mj-tab-dot ${t.dot}`}></span>}
              {t.label}
              {t.count !== null && <span className="mj-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      {pagination.total > 0 && (
        <p className="mj-results-info">
          Showing {((currentPage - 1) * 10) + 1}–{Math.min(currentPage * 10, pagination.total)} of {pagination.total} jobs
        </p>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="mj-empty">
          <span className="material-symbols-outlined">work_off</span>
          <h3>No jobs found</h3>
          <p>Post your first job to start finding great candidates</p>
          <Link to="/employer/post-job" className="mj-cta-btn">
            <span className="material-symbols-outlined">add</span>Post a Job
          </Link>
        </div>
      ) : (
        <div className="mj-list">
          {jobs.map((job) => {
            const cfg = getStatusCfg(job.status);
            return (
              <div key={job._id} className="mj-item">
                <div className="mj-item-left">
                  <div className="mj-item-icon">
                    <span className="material-symbols-outlined">work</span>
                  </div>
                  <div className="mj-item-body">
                    <div className="mj-item-title-row">
                      <span className="mj-item-title">
                        {job.title}
                      </span>
                      <span className={`mj-status-badge ${cfg.color}`}>
                        <span className="material-symbols-outlined">{cfg.icon}</span>
                        {cfg.label}
                      </span>
                      {job.featured && <span className="mj-badge-featured"><span className="material-symbols-outlined">star</span>Featured</span>}
                      {job.urgent && <span className="mj-badge-urgent"><span className="material-symbols-outlined">priority_high</span>Urgent</span>}
                    </div>
                    <div className="mj-item-meta">
                      {job.location?.city?.name && (
                        <span><span className="material-symbols-outlined">location_on</span>{job.location.city.name}</span>
                      )}
                      {job.jobType?.name && (
                        <span><span className="material-symbols-outlined">business_center</span>{job.jobType.name}</span>
                      )}
                      <span><span className="material-symbols-outlined">schedule</span>Posted {getTimeAgo(job.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="mj-item-right">
                  <div className="mj-item-counters">
                    <div className="mj-counter">
                      <span className="material-symbols-outlined">visibility</span>
                      <strong>{job.views || 0}</strong>
                      <span>Views</span>
                    </div>
                    <div className="mj-counter">
                      <span className="material-symbols-outlined">group</span>
                      <strong>{job.applications || 0}</strong>
                      <span>Applied</span>
                    </div>
                  </div>

                  <div className="mj-item-actions">
                    <button className="mj-action-btn" onClick={() => handleActionClick('edit', job._id)} title="Edit">
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button className="mj-action-btn" onClick={() => handleActionClick('applications', job._id)} title="Applications">
                      <span className="material-symbols-outlined">description</span>
                    </button>
                    <div className="mj-dropdown-wrap">
                      <button className="mj-action-btn" onClick={() => toggleDropdown(job._id)} title="More">
                        <span className="material-symbols-outlined">more_vert</span>
                      </button>
                      {activeDropdown === job._id && (
                        <div className="mj-dropdown">
                          {job.status === 'draft' && (
                            <button className="mj-dd-item success" onClick={() => handleActionClick('publish', job._id)}>
                              <span className="material-symbols-outlined">publish</span>Publish
                            </button>
                          )}
                          {job.status === 'active' && (<>
                            <button className="mj-dd-item warning" onClick={() => handleActionClick('hold', job._id)}>
                              <span className="material-symbols-outlined">pause_circle</span>Put on Hold
                            </button>
                            <button className="mj-dd-item secondary" onClick={() => handleActionClick('close', job._id)}>
                              <span className="material-symbols-outlined">lock</span>Close Job
                            </button>
                          </>)}
                          {job.status === 'on_hold' && (
                            <button className="mj-dd-item success" onClick={() => handleActionClick('reopen', job._id)}>
                              <span className="material-symbols-outlined">play_circle</span>Activate
                            </button>
                          )}
                          {job.status === 'closed' && (
                            <button className="mj-dd-item success" onClick={() => handleActionClick('reopen', job._id)}>
                              <span className="material-symbols-outlined">lock_open</span>Reopen
                            </button>
                          )}
                          <div className="mj-dd-divider"></div>
                          <button className="mj-dd-item danger" onClick={() => handleActionClick('delete', job._id)}>
                            <span className="material-symbols-outlined">delete</span>Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default MyJobs;
